import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';

const PHASES = [
  { id: 'initiation', label: 'Initiation', color: '#8B5CF6', bg: '#F5F3FF' },
  { id: 'planning',   label: 'Planning',   color: '#3B82F6', bg: '#EFF6FF' },
  { id: 'execution',  label: 'Execution',  color: '#10B981', bg: '#ECFDF5' },
  { id: 'close_out',  label: 'Close Out',  color: '#F59E0B', bg: '#FFFBEB' },
];
const PHASE_MAP = Object.fromEntries(PHASES.map(p => [p.id, p]));

export default function ProjectPlanTemplatesPage() {
  const { currentUser } = useApp();
  const [templates, setTemplates]     = useState([]);
  const [selected, setSelected]       = useState(null);
  const [tasks, setTasks]             = useState([]);
  const [showCreate, setShowCreate]   = useState(false);
  const [editTpl, setEditTpl]         = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [editTask, setEditTask]       = useState(null);
  const [visFilter, setVisFilter]     = useState('both'); // 'both' | 'public' | 'private'

  const loadTemplates = () => {
    api.get('project-plan-templates').then(setTemplates).catch(() => {});
  };

  const loadTasks = useCallback((tplId) => {
    api.get(`project-plan-templates/${tplId}/tasks`).then(setTasks).catch(() => setTasks([]));
  }, []);

  useEffect(loadTemplates, []);
  useEffect(() => { if (selected) loadTasks(selected.id); }, [selected, loadTasks]);

  const createTemplate = async (data) => {
    const res = await api.post('project-plan-templates', data);
    setShowCreate(false);
    loadTemplates();
    setSelected({ ...data, id: res.id, task_count: 0 });
  };

  const saveTemplate = async (data) => {
    await api.put(`project-plan-templates/${editTpl.id}`, data);
    setEditTpl(null);
    loadTemplates();
    setSelected(prev => prev?.id === editTpl.id ? { ...prev, ...data } : prev);
  };

  const deleteTemplate = async (id) => {
    if (!confirm('Delete this template and all its tasks?')) return;
    await api.delete(`project-plan-templates/${id}`);
    if (selected?.id === id) setSelected(null);
    loadTemplates();
  };

  const addTask = async (data) => {
    await api.post(`project-plan-templates/${selected.id}/tasks`, { ...data, sort_order: tasks.length });
    setShowAddTask(false);
    loadTasks(selected.id);
    loadTemplates();
  };

  const saveTask = async (data) => {
    await api.put(`project-plan-templates/${selected.id}/tasks/${editTask.id}`, data);
    setEditTask(null);
    loadTasks(selected.id);
  };

  const deleteTask = async (taskId) => {
    if (!confirm('Remove this task from the template?')) return;
    await api.delete(`project-plan-templates/${selected.id}/tasks/${taskId}`);
    loadTasks(selected.id);
    loadTemplates();
  };

  // Derive which phases are used by the selected template's tasks
  const phasesUsed = [...new Set(tasks.map(t => t.phase || 'execution'))];

  return (
    <div className="fade-in">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h2 className="text-base font-extrabold text-navy">Project Plan Templates</h2>
          <div className="text-[9px] text-gray-500">Reusable task structures — apply to any SOW from the Plan tab. Public templates are shared with all team members.</div>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 whitespace-nowrap flex-shrink-0">
          + New Template
        </button>
      </div>

      <div className="flex gap-3 items-start">
        {/* Template list (left panel). On mobile it fills the width; once a
            template is selected the detail takes over and the list is hidden
            (reachable again via the detail's "← Back" button). */}
        <div className={`${selected ? 'hidden md:block' : ''} w-full md:w-72 flex-shrink-0 space-y-1.5`}>
          {/* Visibility filter */}
          <div className="flex rounded border border-gray-200 overflow-hidden mb-1">
            {[['both','All'],['public','🌐 Public'],['private','🔒 Private']].map(([v, label]) => (
              <button key={v} onClick={() => setVisFilter(v)}
                className={`flex-1 py-1 text-[8px] font-semibold transition-colors ${visFilter === v ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
          {templates.filter(t => visFilter === 'both' || (visFilter === 'private' ? t.visibility === 'private' : t.visibility !== 'private')).length === 0 && (
            <div className="bg-white border border-gray-200 rounded p-6 text-center text-[10px] text-gray-400">
              No {visFilter !== 'both' ? visFilter + ' ' : ''}templates yet.
            </div>
          )}
          {templates.filter(t => visFilter === 'both' || (visFilter === 'private' ? t.visibility === 'private' : t.visibility !== 'private')).map(t => (
            <div
              key={t.id}
              onClick={() => setSelected(t)}
              className={`bg-white border rounded p-2.5 cursor-pointer transition-all ${selected?.id === t.id ? 'border-gold bg-gold/5 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-start justify-between gap-1">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] font-semibold truncate">{t.name}</span>
                    {/* Visibility badge */}
                    {t.visibility === 'private'
                      ? <span className="text-[7px] px-1 py-0.5 rounded bg-gray-100 text-gray-500 font-semibold flex-shrink-0">🔒 Private</span>
                      : <span className="text-[7px] px-1 py-0.5 rounded bg-blue-50 text-blue-600 font-semibold flex-shrink-0">🌐 Public</span>
                    }
                  </div>
                  {t.description && <div className="text-[8px] text-gray-400 truncate">{t.description}</div>}
                  <div className="text-[8px] text-gray-400 mt-1">{t.task_count || 0} task{t.task_count !== 1 ? 's' : ''}</div>
                  {/* Created by */}
                  {t.created_by_name && <div className="text-[7px] text-gray-300 mt-0.5">by {t.created_by_name}</div>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={e => { e.stopPropagation(); setEditTpl(t); }} className="text-[8px] text-gold hover:underline">Edit</button>
                  <button onClick={e => { e.stopPropagation(); deleteTemplate(t.id); }} className="text-[8px] text-red-400 hover:text-red-600">Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Task editor (right panel). Hidden on mobile until a template is
            selected, so small screens show the list first. */}
        <div className={`${selected ? '' : 'hidden md:block'} flex-1 min-w-0`}>
          {!selected ? (
            <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">
              Select a template on the left to view and edit its tasks
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              {/* Template header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gold/5 border-b border-gray-200">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSelected(null)} className="md:hidden -ml-1 text-gray-400 hover:text-navy text-[11px] font-semibold flex-shrink-0">← Back</button>
                    <span className="text-[12px] font-bold text-navy truncate">{selected.name}</span>
                    {selected.visibility === 'private'
                      ? <span className="text-[7px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-semibold">🔒 Private</span>
                      : <span className="text-[7px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-semibold">🌐 Shared with team</span>
                    }
                  </div>
                  {selected.description && <div className="text-[9px] text-gray-500 mt-0.5">{selected.description}</div>}
                  {/* Phase chips */}
                  {phasesUsed.length > 0 && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {PHASES.filter(ph => phasesUsed.includes(ph.id)).map(ph => (
                        <span key={ph.id} className="text-[7px] px-1.5 py-0.5 rounded font-semibold" style={{ background: ph.bg, color: ph.color }}>
                          {ph.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setShowAddTask(true)} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80 flex-shrink-0">
                  + Add Task
                </button>
              </div>

              {tasks.length === 0 ? (
                <div className="p-8 text-center text-[10px] text-gray-400">
                  No tasks in this template yet. Add tasks to define the plan structure.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold w-6">#</th>
                      <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Task Name</th>
                      <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Phase</th>
                      <th className="px-2 py-1.5 text-right text-[7.5px] uppercase text-gray-400 font-bold">Days</th>
                      <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Depends On</th>
                      <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Type</th>
                      <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Team</th>
                      <th className="px-2 py-1.5 w-16"></th>
                    </tr></thead>
                    <tbody>
                      {tasks.map((t, idx) => {
                        const ph = PHASE_MAP[t.phase || 'execution'];
                        return (
                          <tr key={t.id} className="border-b border-gray-100 hover:bg-gold/5">
                            <td className="px-2 py-1.5 text-[9px] text-gray-400">{idx + 1}</td>
                            <td className="px-2 py-1.5">
                              <div className="text-[10px] font-semibold">{t.name}</div>
                              {t.description && <div className="text-[8px] text-gray-400 truncate max-w-[180px]">{t.description}</div>}
                            </td>
                            <td className="px-2 py-1.5">
                              {ph && (
                                <span className="text-[7px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap" style={{ background: ph.bg, color: ph.color }}>
                                  {ph.label}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-[10px] font-semibold text-right">{t.duration_days}d</td>
                            <td className="px-2 py-1.5 text-[9px] text-gray-500">{t.depends_on_name || '—'}</td>
                            <td className="px-2 py-1.5">
                              <span className={`text-[7px] font-bold px-1 py-0.5 rounded ${t.task_type === 'checklist' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                {t.task_type}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 text-[9px] text-gray-500">{t.team_name || '—'}</td>
                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                              <button onClick={() => setEditTask(t)} className="text-[8px] text-gold hover:underline mr-1">Edit</button>
                              <button onClick={() => deleteTask(t.id)} className="text-[8px] text-red-400 hover:text-red-600">×</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td colSpan="3" className="px-2 py-1.5 text-[9px] text-gray-500 font-semibold text-right">Total Duration</td>
                        <td className="px-2 py-1.5 text-[10px] font-bold text-right text-navy">
                          {tasks.reduce((s, t) => s + (t.duration_days || 0), 0)}d
                        </td>
                        <td colSpan="4" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreate && <TemplateModal title="New Template" onClose={() => setShowCreate(false)} onSave={createTemplate} />}
      {editTpl    && <TemplateModal title={`Edit: ${editTpl.name}`} template={editTpl} onClose={() => setEditTpl(null)} onSave={saveTemplate}
        mayChangeVisibility={!editTpl.created_by || editTpl.created_by === currentUser?.id || canAccess('admin')} />}
      {showAddTask && selected && <TemplateTaskModal title="Add Task" existingTasks={tasks} onClose={() => setShowAddTask(false)} onSave={addTask} />}
      {editTask   && selected && <TemplateTaskModal title={`Edit: ${editTask.name}`} task={editTask} existingTasks={tasks} onClose={() => setEditTask(null)} onSave={saveTask} />}
    </div>
  );
}

// Who sees a template is its owner's call (the server refuses anybody else),
// so somebody improving a shared template sees its visibility but cannot flip
// it. A refusal is shown here in the dialog, where Save was pressed.
function TemplateModal({ title, template, onClose, onSave, mayChangeVisibility = true }) {
  const [form, setForm] = useState({
    name:        template?.name || '',
    description: template?.description || '',
    visibility:  template?.visibility || 'public',
  });
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) return;
    setError('');
    try { await onSave(form); } catch (e) { setError(e?.message || 'Saving failed. Please try again.'); }
  };

  return (
    <Modal title={title} onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={save} disabled={!form.name.trim()} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Save</button></>
    }>
      <div className="space-y-3">
        {error && <div className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Template Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Standard Network Deployment..." autoFocus />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <textarea className="form-textarea" rows="2" value={form.description} onChange={e => set('description', e.target.value)} placeholder="What project type is this template for?" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Visibility</label>
          {!mayChangeVisibility ? (
            <div className="text-[9px] text-gray-600">
              {form.visibility === 'private' ? '🔒 Private' : '🌐 Public'}
              <span className="text-[8px] text-gray-400"> · only {template?.created_by_name || 'the person who made it'} can change who sees this template</span>
            </div>
          ) : (
          <div className="flex gap-4">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="tpl-vis" value="public" checked={form.visibility === 'public'} onChange={() => set('visibility', 'public')} className="accent-gold" />
              <span className="text-[9px] font-semibold text-gray-700">🌐 Public</span>
              <span className="text-[8px] text-gray-400">— available to all team members</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="tpl-vis" value="private" checked={form.visibility === 'private'} onChange={() => set('visibility', 'private')} className="accent-gold" />
              <span className="text-[9px] font-semibold text-gray-700">🔒 Private</span>
              <span className="text-[8px] text-gray-400">— only visible to you</span>
            </label>
          </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function TemplateTaskModal({ title, task, existingTasks, onClose, onSave }) {
  const [teams, setTeams] = useState([]);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [form, setForm] = useState({
    name:                  task?.name || '',
    description:           task?.description || '',
    duration_days:         task?.duration_days ?? 1,
    depends_on_task_id:    task?.depends_on_task_id || '',
    dep_type:              task?.dep_type || 'FS',
    lag_days:              task?.lag_days ?? 0,
    lag_unit:              task?.lag_unit || 'workdays',
    parent_task_id:        task?.parent_task_id || '',
    is_summary:            task?.is_summary ? true : false,
    is_milestone:          task?.is_milestone ? true : false,
    duration_unit:         task?.duration_unit || 'workdays',
    task_type:             task?.task_type || 'custom',
    team_id:               task?.team_id || '',
    checklist_template_id: task?.checklist_template_id || '',
    phase:                 task?.phase || 'execution',
    sort_order:            task?.sort_order ?? existingTasks.length,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const otherTasks = existingTasks.filter(t => t.id !== task?.id);

  useEffect(() => {
    api.get('teams').then(setTeams).catch(() => setTeams([]));
    api.get('repeatable-templates').then(setChecklistTemplates).catch(() => setChecklistTemplates([]));
  }, []);

  const isChecklist = form.task_type === 'checklist';
  const valid = form.name.trim() && form.team_id && (!isChecklist || form.checklist_template_id);

  return (
    <Modal title={title} onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button
          onClick={() => { if (valid) onSave({ ...form, duration_days: form.is_milestone ? 0 : (parseFloat(form.duration_days) || 1), lag_days: parseFloat(form.lag_days) || 0, depends_on_task_id: form.depends_on_task_id || null, parent_task_id: form.parent_task_id || null, is_summary: form.is_summary ? 1 : 0, is_milestone: form.is_milestone ? 1 : 0, team_id: form.team_id || null, checklist_template_id: form.checklist_template_id || null }); }}
          disabled={!valid}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40"
        >Save</button></>
    }>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Task Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Site survey, Configure switches..." autoFocus />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional detail..." />
        </div>
        <div className="col-span-2 flex items-center gap-4 pb-1 border-b border-gray-100">
          <span className="text-[8px] font-bold text-gray-500 uppercase">Row type</span>
          {[['task', 'Task'], ['summary', 'Summary bar'], ['milestone', 'Milestone']].map(([id, label]) => (
            <label key={id} className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="tpl-rowtype" className="accent-navy"
                     checked={id === 'summary' ? form.is_summary : id === 'milestone' ? form.is_milestone : (!form.is_summary && !form.is_milestone)}
                     onChange={() => { set('is_summary', id === 'summary'); set('is_milestone', id === 'milestone'); }} />
              <span className="text-[9px] font-semibold text-gray-700">{label}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sits under</label>
          <select className="form-select" value={form.parent_task_id} onChange={e => set('parent_task_id', e.target.value)}>
            <option value="">— top level —</option>
            {otherTasks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Phase</label>
          <select className="form-select" value={form.phase} onChange={e => set('phase', e.target.value)}>
            {PHASES.map(ph => <option key={ph.id} value={ph.id}>{ph.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Duration</label>
          <div className="flex gap-1">
            <input className="form-input flex-1" type="number" min="0" step="0.5" disabled={form.is_milestone || form.is_summary}
                   value={form.is_milestone ? 0 : form.duration_days} onChange={e => set('duration_days', e.target.value)} />
            <select className="form-select w-28" value={form.duration_unit} onChange={e => set('duration_unit', e.target.value)}>
              <option value="workdays">working days</option>
              <option value="calendar">calendar days</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Task Type</label>
          <select className="form-select" value={form.task_type} onChange={e => set('task_type', e.target.value)}>
            <option value="custom">Custom</option>
            <option value="checklist">Checklist</option>
          </select>
        </div>
        {isChecklist && (
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Checklist Template <span className="text-red-500">*</span></label>
            <select className={`form-select ${!form.checklist_template_id ? 'border-amber-400' : ''}`} value={form.checklist_template_id} onChange={e => set('checklist_template_id', e.target.value)}>
              <option value="">Select a checklist template...</option>
              {checklistTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Team *</label>
          <select className={`form-select ${!form.team_id ? 'border-amber-400' : ''}`} value={form.team_id} onChange={e => set('team_id', e.target.value)}>
            <option value="">— select team —</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Depends On</label>
          <select className="form-select" value={form.depends_on_task_id} onChange={e => set('depends_on_task_id', e.target.value)}>
            <option value="">None</option>
            {otherTasks.map(t => {
              const ph = PHASE_MAP[t.phase || 'execution'];
              return <option key={t.id} value={t.id}>[{ph?.label}] {t.name}</option>;
            })}
          </select>
          {form.depends_on_task_id && (
            <div className="flex gap-1 mt-1">
              <select className="form-select w-16 text-[9px]" value={form.dep_type} onChange={e => set('dep_type', e.target.value)}>
                <option value="FS">FS</option><option value="SS">SS</option>
                <option value="FF">FF</option><option value="SF">SF</option>
              </select>
              <input className="form-input w-16 text-[9px]" type="number" step="0.5" value={form.lag_days}
                     title="Lag. Negative is a lead." onChange={e => set('lag_days', e.target.value)} />
              <select className="form-select flex-1 text-[9px]" value={form.lag_unit} onChange={e => set('lag_unit', e.target.value)}>
                <option value="workdays">working days</option>
                <option value="calendar">calendar days</option>
              </select>
            </div>
          )}
          <div className="text-[7.5px] text-gray-400 mt-0.5">
            FS starts after it finishes, SS after it starts, FF finishes after it finishes. Lag can be negative to overlap.
          </div>
        </div>
      </div>
    </Modal>
  );
}
